// src/shared/hooks/useVendorSeasonList.ts
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { vendorSeasonListApiService } from '../service/api/vendorSeasonListApiService';
import { FetchResult } from 'type/apiType';
import {
  VendorSeasonListBulkBySeasonRequest,
  VendorSeasonListBulkCreateRequest,
  VendorSeasonListBulkCreateResponse,
  VendorSeasonListCreateRequest,
  VendorSeasonListPatchRequest,
  VendorSeasonListQueryParams,
  VendorSeasonListRow,
} from 'type/vendorSeasonListType';

export const VENDOR_SEASON_LIST_QUERY_KEYS = {
  all: ['vendor-season-list'] as const,
  lists: () => [...VENDOR_SEASON_LIST_QUERY_KEYS.all, 'list'] as const,
  list: (params?: VendorSeasonListQueryParams) =>
    [...VENDOR_SEASON_LIST_QUERY_KEYS.lists(), params ?? {}] as const,
};

export const useVendorSeasonList = (
  params?: VendorSeasonListQueryParams,
  options?: Omit<UseQueryOptions<FetchResult<VendorSeasonListRow>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: VENDOR_SEASON_LIST_QUERY_KEYS.list(params),
    queryFn: () => vendorSeasonListApiService.fetchList(params),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateVendorSeasonMapping = () => {
  const queryClient = useQueryClient();
  return useMutation<VendorSeasonListRow, unknown, VendorSeasonListCreateRequest>({
    mutationFn: body => vendorSeasonListApiService.createMapping(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VENDOR_SEASON_LIST_QUERY_KEYS.all });
    },
  });
};

export const useBulkCreateVendorSeasonMappings = () => {
  const queryClient = useQueryClient();
  return useMutation<
    VendorSeasonListBulkCreateResponse,
    unknown,
    VendorSeasonListBulkCreateRequest
  >({
    mutationFn: body => vendorSeasonListApiService.bulkCreateMappings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VENDOR_SEASON_LIST_QUERY_KEYS.all });
    },
  });
};

export const useBulkCreateVendorSeasonMappingsBySeason = () => {
  const queryClient = useQueryClient();
  return useMutation<
    VendorSeasonListBulkCreateResponse,
    unknown,
    VendorSeasonListBulkBySeasonRequest
  >({
    mutationFn: body => vendorSeasonListApiService.bulkCreateBySeason(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VENDOR_SEASON_LIST_QUERY_KEYS.all });
    },
  });
};

export const usePatchVendorSeasonMapping = () => {
  const queryClient = useQueryClient();
  return useMutation<
    VendorSeasonListRow,
    unknown,
    { id: number; body: VendorSeasonListPatchRequest }
  >({
    mutationFn: ({ id, body }) => vendorSeasonListApiService.patchMapping(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VENDOR_SEASON_LIST_QUERY_KEYS.all });
    },
  });
};

export const useDeleteVendorSeasonMapping = () => {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, number>({
    mutationFn: id => vendorSeasonListApiService.deleteMapping(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VENDOR_SEASON_LIST_QUERY_KEYS.all });
    },
  });
};
